import { Fragment, memo, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { determineCardType, isCreditCardAmex } from "app/utils/creditCardUtils";
import { paymentTypesValues } from "app/utils/propTypes";
import { CB_NUMBER_MAX_LENGTH, CREDIT_CARDS_TYPES, PAYMENT_METHODS } from "app/constants";

import "./CreditCardFieldset.scss";
import FloatingButton from "app/pages/.shared/Popover/FloatingButton";
import InputFormik from "app/pages/.shared/form/InputFormik";
import { useField, useFormikContext } from "formik";
import { dependantPaymentCardFields } from "app/pages/Booking/Payment/paymentSchema";
import last from "lodash/last";
import { isEmpty } from "lodash";
import AdyenPayment from "app/pages/Booking/Payment/PaymentData/AdyenPayment/AdyenPayment";
import get from "lodash/get";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

const CreditCardFieldset = ({
	showAmountToPay,
	isAmountToPayRequired,
	selectedPaymentType,
	isAmexAvailable,
	updateEncryptedCard = () => {},
	onAdyenComponentMounted = () => {},
	useAdyen = true,
	acceptedCreditCardTypes = [],
}) => {
	const cardNumberInputName =
		selectedPaymentType === PAYMENT_METHODS.BCMC
			? "payment.cardBCMC.number"
			: "payment.card.number";
	const [field] = useField(cardNumberInputName);
	const [cvcMaxLength, setCvcMaxLength] = useState(3);
	const [cardType, setCardType] = useState(undefined);
	const { values, setFieldError, errors } = useFormikContext();
	const { track } = useAnalytics();

	useEffect(() => {
		if (field?.value) {
			setCardType(determineCardType(field?.value));
		}
	}, [field?.value]);

	const handleCreditCardInformationChange = event => {
		const { name: touchedFieldName } = event.target;

		const name = last(touchedFieldName.split("."));
		const cardPathName = selectedPaymentType === PAYMENT_METHODS.BCMC ? "cardBCMC" : "card";
		dependantPaymentCardFields.forEach(fieldName => {
			if (fieldName !== name) {
				setFieldError(`payment.${cardPathName}.${fieldName}`);
			}
			if (isEmpty(get(errors.payment, `.${cardPathName}.${fieldName}`))) {
				setFieldError("payment");
			}
		});
	};

	const changeCvcField = event => {
		handleCreditCardInformationChange(event);
		if (values?.payment?.card?.number) {
			setCvcMaxLength(isCreditCardAmex(values.payment.card.number) ? 4 : 3);
		}
	};

	const onCreditCardDataChange = useCallback(data => {
		setCardType(data.brand);
		updateEncryptedCard(data);
		setFieldError("payment"); // supprimer les messages d'erreurs formik
	}, []);

	const handleAdyenError = useCallback(error => {
		track("adyen_error", {
			errorName: error?.errorName,
			errorMessage: error?.errorMessage,
		});
	}, []);

	// for data-browsing-ignore usage, see https://help.crisp.chat/en/article/how-to-prevent-elements-from-being-tracked-by-magicbrowse-ju7mcg/
	return (
		<div className="creditcard" data-browsing-ignore data-testid="credit-card-form">
			{showAmountToPay && (
				<div className="creditcard__item creditcard__item--amount">
					<InputFormik
						type="number"
						label={
							<span>
								<FormattedMessage id="creditcard.amount.label" />
								{isAmountToPayRequired ? "*" : ""}
							</span>
						}
						data-testid="creditcard-amount-input"
						name="payment.card.amount"
						id="creditcard-amount"
					/>
				</div>
			)}
			{useAdyen ? (
				<AdyenPayment
					acceptedCreditCardTypes={acceptedCreditCardTypes}
					onChange={onCreditCardDataChange}
					onAdyenComponentMounted={onAdyenComponentMounted}
					onError={handleAdyenError}
				/>
			) : (
				<>
					<div className="creditcard__item creditcard__item--holdername">
						<InputFormik
							data-testid="creditcard-holdername-input"
							label={<FormattedMessage id="creditcard.holdername.label" />}
							name={
								selectedPaymentType === PAYMENT_METHODS.BCMC
									? "payment.cardBCMC.holderName"
									: "payment.card.holderName"
							}
							autoComplete="cc-name"
							id="creditcard-holdername"
						/>
					</div>
					<div className="creditcard__item creditcard__item--number">
						<InputFormik
							data-testid="creditcard-number-input"
							type="number"
							data-field-name="number"
							label={<FormattedMessage id="creditcard.number.label" />}
							name={
								selectedPaymentType === PAYMENT_METHODS.BCMC
									? "payment.cardBCMC.number"
									: "payment.card.number"
							}
							id={
								selectedPaymentType === PAYMENT_METHODS.BCMC
									? "creditcard-bcmc-number"
									: "creditcard-number"
							}
							autoComplete="cc-number"
							maxLength={CB_NUMBER_MAX_LENGTH}
							onChange={handleCreditCardInformationChange}
						/>

						<div className="creditcard__type-picto">
							<i className={`icon icon--${cardType}-small`} />
						</div>
					</div>

					<div className="creditcard__item creditcard__item--expiration-month">
						<InputFormik
							data-testid="creditcard-expmonth-input"
							type="number"
							id="expiration-month"
							name={
								selectedPaymentType === PAYMENT_METHODS.BCMC
									? "payment.cardBCMC.expiryMonth"
									: "payment.card.expiryMonth"
							}
							data-field-name="expiryMonth"
							autocomplete="cc-exp-month"
							maxLength={2}
							label={<FormattedMessage id="creditcard.expiryMonth.label" />}
							onChange={handleCreditCardInformationChange}
						/>
					</div>

					<div className="creditcard__item creditcard__item--expiration-year">
						<InputFormik
							data-testid="creditcard-expyear-input"
							type="number"
							id="expiration-year"
							name={
								selectedPaymentType === PAYMENT_METHODS.BCMC
									? "payment.cardBCMC.expiryYear"
									: "payment.card.expiryYear"
							}
							data-field-name="expiryYear"
							autocomplete="cc-exp-year"
							maxLength={2}
							label={<FormattedMessage id="creditcard.expiryYear.label" />}
							onChange={handleCreditCardInformationChange}
						/>
					</div>
					{selectedPaymentType !== PAYMENT_METHODS.BCMC && (
						<div className="creditcard__item creditcard__item--cvc">
							<InputFormik
								data-testid="creditcard-cvc-input"
								type="number"
								label={
									<FormattedMessage
										id={
											cardType === CREDIT_CARDS_TYPES.MAESTRO
												? "creditcard.maestro.cvc.label"
												: "creditcard.cvc.label"
										}
									/>
								}
								name="payment.card.cvc"
								data-field-name="cvc"
								id="creditcard-cvc"
								maxLength={cvcMaxLength}
								autoComplete="cc-csc"
								onChange={changeCvcField}
							/>

							<div className="creditcard__info-picto">
								<FloatingButton
									position="top"
									popoverClassName="creditcard__popover"
									floatingContent={
										<div className="cvc-info">
											<div className="cvc-info__item">
												<FormattedMessage id="payment.cvc.text" />
											</div>
											<Fragment>
												{isAmexAvailable && (
													<div className="cvc-info__item">
														<FormattedMessage id="payment.cvc.amex.text" />
													</div>
												)}

												<div className="cvc-info__pictos">
													<i className="icon icon--cvc-cb" />
													{isAmexAvailable && (
														<i className="icon icon--cvc-amex" />
													)}
												</div>
											</Fragment>
										</div>
									}
									referenceComponent={<i className="icon icon--info-grey" />}
									floatingOffsetMobile={[-11, 0]}
								/>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

CreditCardFieldset.propTypes = {
	updateEncryptedCard: PropTypes.func,
	onAdyenComponentMounted: PropTypes.func,
	acceptedCreditCardTypes: PropTypes.array,
	useAdyen: PropTypes.bool,
	selectedPaymentType: PropTypes.oneOfType([paymentTypesValues]),
	showAmountToPay: PropTypes.bool,
	isAmountToPayRequired: PropTypes.bool,
	isAmexAvailable: PropTypes.bool,
};

export default memo(CreditCardFieldset);
